import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { REVENUE_INTELLIGENCE_COPY } from "../lib/revenueIntelligenceCopy";
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Clock3,
  Crosshair,
  Database,
  ShieldAlert,
  Sparkles,
  TrendingDown,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AnalysisInfoTooltip } from "../state-of-sales-v2/AnalysisNarrative";
import type {
  ForecastDiagnosisDriver,
  ForecastDiagnosisScenario,
  ForecastDiagnosisScenarioCondition,
  ForecastDiagnosisStory,
} from "./forecastDiagnosisStory";
import {
  getDealEntityDisplayName,
  getDealEntityTooltipText,
} from "./dealEntityCopy";

export type ForecastDependencyItem = {
  dealCount: number;
  dimension: string;
  id: string;
  label: string;
  share: number;
  value: number;
};

function formatMoneyParts(value: number, currencyCode: string) {
  const absolute = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (absolute >= 1_000_000) {
    const millions = absolute / 1_000_000;
    const fractionDigits = Number.isInteger(millions * 10) ? 1 : 2;
    return { amount: `${sign}${millions.toFixed(fractionDigits)}`, unit: `M${currencyCode}` };
  }
  if (absolute >= 1_000) return { amount: `${sign}${(absolute / 1_000).toFixed(1)}`, unit: `k${currencyCode}` };
  return { amount: `${sign}${Math.round(absolute)}`, unit: currencyCode };
}

function formatMoney(value: number, currencyCode: string) {
  const { amount, unit } = formatMoneyParts(value, currencyCode);
  return `${amount} ${unit}`;
}

function DealEntityName({
  detail,
  name,
  onClick,
}: {
  detail?: string | null;
  name: string;
  onClick?: () => void;
}) {
  const tooltipText = getDealEntityTooltipText(name, detail);
  return (
    <span className="inline-flex min-w-0 max-w-full items-start gap-1.5">
      <span className="min-w-0 text-[10px] leading-4">
        <span className="font-medium uppercase tracking-[0.08em] text-gray-400">Deal · </span>
        {onClick ? (
          <button
            className="max-w-full text-left align-top font-semibold text-[#3C2768] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E] focus-visible:ring-offset-1"
            onClick={onClick}
            type="button"
          >
            {getDealEntityDisplayName(name)}
          </button>
        ) : (
          <span className="font-semibold text-[#3C2768]">{getDealEntityDisplayName(name)}</span>
        )}
      </span>
      {tooltipText && (
        <AnalysisInfoTooltip compact label={`${name} Deal details`} text={tooltipText} />
      )}
    </span>
  );
}

const driverStyles = {
  supportive: {
    accent: "#1D819A",
    background: "#F2F9FA",
    border: "#B9DDE3",
    icon: CheckCircle2,
    ribbon: "#4FA0AF",
  },
  unavailable: {
    accent: "#6B7280",
    background: "#F8FAFC",
    border: "#D9DEE7",
    icon: CircleAlert,
    ribbon: "#A8B0BD",
  },
  watch: {
    accent: "#775E97",
    background: "#F8F6FA",
    border: "#DED5E8",
    icon: Clock3,
    ribbon: "#B39BC7",
  },
  weak: {
    accent: "#9F3A4A",
    background: "#FCF6F7",
    border: "#E8C7CD",
    icon: ShieldAlert,
    ribbon: "#D96578",
  },
} as const;

function getDriverRoleCopy(driver: ForecastDiagnosisDriver) {
  if (driver.status === "unavailable") return "Unavailable";
  if (driver.flowProminence === "supporting") return "No material risk";
  return "Key driver";
}

function getDriverDisplayLabel(label: string) {
  return label
    .replace("Largest weakness · ", "")
    .replace("Supporting factor · ", "");
}

function ForecastEndpoint({
  kind,
  story,
}: {
  kind: "crm" | "revelate";
  story: ForecastDiagnosisStory;
}) {
  const isCrm = kind === "crm";
  const gap = story.baselineForecast - story.expectedOutcome;
  const GapIcon = gap >= 0 ? TrendingDown : ArrowUpRight;
  const heroAmount = formatMoneyParts(
    isCrm ? story.baselineForecast : story.expectedOutcome,
    story.currencyCode,
  );

  return (
    <div className={cn(
      "rounded-xl border bg-white p-4 shadow-[0_14px_36px_rgba(35,25,66,0.10)]",
      isCrm ? "border-[#DCD6E3]" : "border-[#C8E2E6] bg-[#F7FBFC]",
    )}>
      <div className={cn(
        "flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em]",
        isCrm ? "text-[#4A4263]" : "text-[#17697B]",
      )}>
        {isCrm ? <Database className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
        {isCrm ? story.baseline.label : REVENUE_INTELLIGENCE_COPY.forecast.revelateForecast}
      </div>
      <p className="mt-4 flex flex-col items-start gap-1 whitespace-nowrap leading-none tabular-nums text-[#2E254B] xl:flex-row xl:items-baseline xl:gap-1.5">
        <span className="text-[46px] font-medium tracking-[-0.025em]">{heroAmount.amount}</span>
        <span className="text-[14px] font-medium tracking-[0.035em] text-[#756D82]">{heroAmount.unit}</span>
      </p>
      {!isCrm && (
        <div className="mt-4 rounded-lg bg-[#E8F4F6] px-3 py-2.5 text-xs leading-5 text-[#17697B]">
          <span className="flex items-center gap-1.5 font-semibold">
            <GapIcon className="h-3.5 w-3.5" /> {formatMoney(Math.abs(gap), story.currencyCode)} {gap >= 0 ? "below" : "above"} CRM baseline
          </span>
          <span className="mt-0.5 block text-[11px] text-[#316C78]">{REVENUE_INTELLIGENCE_COPY.forecast.forecastSupport}</span>
        </div>
      )}
    </div>
  );
}

function FlowDriverButton({
  driver,
  onOpen,
}: {
  driver: ForecastDiagnosisDriver;
  onOpen: () => void;
}) {
  const style = driverStyles[driver.status];
  const Icon = style.icon;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-2.5 rounded-[10px] border px-3 text-left shadow-[0_4px_16px_rgba(35,25,66,0.08)] backdrop-blur-md transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E] focus-visible:ring-offset-2"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.78)",
        borderColor: style.border,
        height: 52,
      }}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white" style={{ backgroundColor: style.ribbon }}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-[#231942]">{getDriverDisplayLabel(driver.label)}</span>
        <span className="mt-0.5 block truncate text-[11px] text-gray-500">{driver.flowSummary}</span>
      </span>
      <span className="hidden shrink-0 text-[10px] font-semibold sm:block" style={{ color: style.accent }}>
        {getDriverRoleCopy(driver)}
      </span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

function DesktopEvidenceFlow({
  onOpen,
  story,
}: {
  onOpen: (driver: ForecastDiagnosisDriver) => void;
  story: ForecastDiagnosisStory;
}) {
  const driverStackHeight = story.drivers.length * 52 + Math.max(0, story.drivers.length - 1) * 18;
  const flowHeight = Math.max(408, driverStackHeight + 76);
  const driverTop = (flowHeight - driverStackHeight) / 2;
  const rowCenters = story.drivers.map((_, index) => driverTop + 26 + index * 70);
  const sourceAnchorY = flowHeight / 2;
  const sourceX = 185;
  const driverLeftX = 300;
  const driverRightX = 700;
  const destinationX = 805;
  // Connections identify related signals; width is never financial attribution.
  const incomingWidths = story.drivers.map(() => 2);
  const sourceGap = 4;
  const sourceAvailableHeight = 108;
  const incomingWidthTotal = incomingWidths.reduce((sum, width) => sum + width, 0);
  const sourceScale = incomingWidthTotal > 0
    ? Math.min(1, (sourceAvailableHeight - Math.max(0, incomingWidths.length - 1) * sourceGap) / incomingWidthTotal)
    : 1;
  const sourceWidths = incomingWidths.map(width => width * sourceScale);
  const outgoingWidths = incomingWidths;
  const getStackCenters = (widths: number[], gap = 0) => {
    const total = widths.reduce((sum, width) => sum + width, 0) + Math.max(0, widths.length - 1) * gap;
    let cursor = sourceAnchorY - total / 2;
    return widths.map(width => {
      const center = cursor + width / 2;
      cursor += width + gap;
      return center;
    });
  };
  const sourceCenters = getStackCenters(sourceWidths, sourceGap);
  const destinationCenters = getStackCenters(outgoingWidths);

  return (
    <div
      className="relative isolate hidden lg:block"
      style={{ height: flowHeight }}
      aria-label="Forecast comparison and related signals"
    >
      <svg aria-hidden="true" className="absolute inset-0 z-0 h-full w-full" preserveAspectRatio="none" viewBox={`0 0 1000 ${flowHeight}`}>
        <defs>
          {story.drivers.map(driver => (
            <linearGradient key={driver.id} id={`flow-${driver.id}`} x1="0" x2="1">
              <stop offset="0" stopColor="#2E1A62" stopOpacity="0.92" />
              <stop offset="0.48" stopColor={driverStyles[driver.status].ribbon} stopOpacity="0.78" />
              <stop offset="1" stopColor="#177486" stopOpacity="0.86" />
            </linearGradient>
          ))}
        </defs>
        {story.drivers.map((driver, index) => {
          const rowY = rowCenters[index];
          const sourceY = sourceCenters[index];
          const destinationY = destinationCenters[index];
          const sourceWidth = sourceWidths[index];
          const incomingWidth = incomingWidths[index];
          const outgoingWidth = outgoingWidths[index];
          const path = [
            `M ${sourceX} ${sourceY - sourceWidth / 2}`,
            `C 225 ${sourceY - sourceWidth / 2}, 260 ${rowY - incomingWidth / 2}, ${driverLeftX} ${rowY - incomingWidth / 2}`,
            `C 430 ${rowY - incomingWidth / 2}, 570 ${rowY - outgoingWidth / 2}, ${driverRightX} ${rowY - outgoingWidth / 2}`,
            `C 740 ${rowY - outgoingWidth / 2}, 765 ${destinationY - outgoingWidth / 2}, ${destinationX} ${destinationY - outgoingWidth / 2}`,
            `L ${destinationX} ${destinationY + outgoingWidth / 2}`,
            `C 765 ${destinationY + outgoingWidth / 2}, 740 ${rowY + outgoingWidth / 2}, ${driverRightX} ${rowY + outgoingWidth / 2}`,
            `C 570 ${rowY + outgoingWidth / 2}, 430 ${rowY + incomingWidth / 2}, ${driverLeftX} ${rowY + incomingWidth / 2}`,
            `C 260 ${rowY + incomingWidth / 2}, 225 ${sourceY + sourceWidth / 2}, ${sourceX} ${sourceY + sourceWidth / 2}`,
            "Z",
          ].join(" ");
          return (
            <path key={driver.id} d={path} fill={`url(#flow-${driver.id})`} />
          );
        })}
      </svg>

      <div className="absolute left-0 top-1/2 z-10 -translate-y-1/2 pr-2" style={{ width: "20.5%" }}>
        <ForecastEndpoint kind="crm" story={story} />
      </div>
      <div className="absolute right-0 top-1/2 z-10 -translate-y-1/2 pl-2" style={{ width: "20.5%" }}>
        <ForecastEndpoint kind="revelate" story={story} />
      </div>

      <div
        className="absolute z-10 flex flex-col"
        style={{ gap: 18, left: "30%", top: driverTop, width: "40%" }}
      >
        {story.drivers.map(driver => (
          <FlowDriverButton key={driver.id} driver={driver} onOpen={() => onOpen(driver)} />
        ))}
      </div>

      <div className="absolute left-0 top-0 z-10 text-center text-[9px] font-semibold uppercase tracking-[0.14em] text-[#6D657B]" style={{ width: "20.5%" }}>
        CRM baseline
      </div>
      <div className="absolute top-0 z-10 text-center text-[9px] font-semibold uppercase tracking-[0.14em] text-[#6D657B]" style={{ left: "30%", width: "40%" }}>
        {REVENUE_INTELLIGENCE_COPY.forecast.forecastSignals}
      </div>
      <div className="absolute right-0 top-0 z-10 text-center text-[9px] font-semibold uppercase tracking-[0.14em] text-[#17697B]" style={{ width: "20.5%" }}>
        Revelate
      </div>
    </div>
  );
}

function MobileEvidenceFlow({
  onOpen,
  story,
}: {
  onOpen: (driver: ForecastDiagnosisDriver) => void;
  story: ForecastDiagnosisStory;
}) {
  return (
    <div className="space-y-4 lg:hidden">
      <ForecastEndpoint kind="crm" story={story} />
      <div className="relative space-y-2.5 border-y border-[#DED8E4] py-4">
        <div className="absolute bottom-0 left-4 top-0 w-px bg-gradient-to-b from-[#2E1A62] via-[#D8CADF] to-[#177486]" />
        {story.drivers.map(driver => (
          <div key={driver.id} className="relative pl-8">
            <FlowDriverButton driver={driver} onOpen={() => onOpen(driver)} />
          </div>
        ))}
      </div>
      <ForecastEndpoint kind="revelate" story={story} />
    </div>
  );
}

function ConfidenceStrip({ story }: { story: ForecastDiagnosisStory }) {
  const score = story.confidence.score;
  const hasMovement = story.confidence.change !== null && story.confidence.changePeriod !== null;
  const MovementIcon = (story.confidence.change ?? 0) >= 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="border-t border-[#DED8E4] bg-[#F9F7FB] px-5 py-4 lg:px-6">
      <div className="grid gap-4 lg:grid-cols-[1.05fr_1.45fr] lg:items-center">
        <div className="flex items-center gap-3 lg:border-r lg:border-[#D9D2E0] lg:pr-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#5E548E] ring-1 ring-[#D9D2E0]">
            <ShieldAlert className="h-5 w-5" />
          </span>
          <div>
            <div className="flex items-center gap-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-[#5E548E]">CRM Forecast Confidence</p>
              <AnalysisInfoTooltip
                compact
                label="CRM Forecast Confidence"
                text={REVENUE_INTELLIGENCE_COPY.forecast.confidenceTooltip}
              />
            </div>
            <p className="mt-1 text-[34px] font-semibold leading-none tracking-[-0.02em] tabular-nums text-[#231942]">
              {score ?? "—"} {score === null ? null : <span className="text-xs font-normal text-gray-400">/ 100</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2">
          {hasMovement ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FCF0F2] px-3 py-1.5 text-xs font-semibold text-[#9F3A4A]">
              <MovementIcon className="h-3.5 w-3.5" /> {Math.abs(story.confidence.change!)} pts {story.confidence.changePeriod}
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#5E548E] ring-1 ring-[#D9D2E0]">
              {story.confidence.label} agreement
            </span>
          )}
          <p className="text-xs leading-5 text-gray-600">
            {story.confidence.label === "Strong"
              ? "CRM and evidence are closely aligned."
              : story.confidence.label === "Caution"
                ? "The endpoints differ enough to review."
                : story.confidence.label === "Weak"
                  ? "The underlying revenue evidence does not support the CRM forecast. Management should be cautious when relying on it."
                  : "Agreement is unavailable for this scope."}
          </p>
        </div>
      </div>
    </div>
  );
}

function ForecastEvidenceFlow({
  onOpen,
  story,
}: {
  onOpen: (driver: ForecastDiagnosisDriver) => void;
  story: ForecastDiagnosisStory;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-[#DED8E4] bg-[radial-gradient(circle_at_50%_0%,rgba(159,134,192,0.10),transparent_38%),#FFFFFF] shadow-sm">
      <div className="flex flex-col gap-3 border-b border-[#E8E3EC] px-5 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-6">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5E548E]">Forecast signals</p>
          <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
            <p>{REVENUE_INTELLIGENCE_COPY.forecast.whyTheGap}</p>
            <Sparkles aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-[#1D819A]" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
          {story.dataMode === "demo" && (
            <span className="rounded-full bg-[#231942] px-2.5 py-1 font-semibold uppercase tracking-[0.11em] text-white">Demo data</span>
          )}
          <span>{story.period}</span>
        </div>
      </div>

      <div className="p-4 lg:px-5 lg:py-6">
        <DesktopEvidenceFlow onOpen={onOpen} story={story} />
        <MobileEvidenceFlow onOpen={onOpen} story={story} />
      </div>

      <ConfidenceStrip story={story} />
    </section>
  );
}

function formatForecastDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function ForecastGapBreakdown({
  onOpenDeal,
  story,
}: {
  onOpenDeal?: (dealId: string) => void;
  story: ForecastDiagnosisStory;
}) {
  const breakdown = story.gapBreakdown;
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const selectedGroup = breakdown?.groups.find(group => group.id === selectedGroupId) ?? null;

  useEffect(() => setShowAll(false), [selectedGroupId]);
  if (!breakdown || breakdown.groups.length === 0) return null;

  const gapDirection = breakdown.netGap > 0 ? "below" : "above";
  const visibleDeals = selectedGroup
    ? showAll ? selectedGroup.deals : selectedGroup.deals.slice(0, 5)
    : [];

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:p-6">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5E548E]">Where the gap sits</p>
        <h2 className="mt-1 text-lg font-semibold text-[#231942]">
          {formatMoney(Math.abs(breakdown.netGap), story.currencyCode)} {gapDirection} CRM
        </h2>
      </div>

      <div className={cn(
        "mt-4 grid gap-3 md:grid-cols-2",
        breakdown.groups.length >= 3 && "lg:grid-cols-3",
      )}>
        {breakdown.groups.map(group => {
          const isAbove = group.direction === "above";
          const isSelected = selectedGroupId === group.id;
          const isEmpty = group.deals.length === 0;
          return (
            <button
              aria-expanded={isSelected}
              className={cn(
                "rounded-xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E] focus-visible:ring-offset-2",
                isAbove ? "border-[#B9DDE3] bg-[#F4FAFB]" : "border-[#DED5E8] bg-[#FAF8FB]",
                isSelected && "ring-2 ring-[#5E548E] ring-offset-2",
                isEmpty && "cursor-default opacity-75 hover:translate-y-0 hover:shadow-none",
              )}
              disabled={isEmpty}
              key={group.id}
              onClick={() => setSelectedGroupId(isSelected ? null : group.id)}
              type="button"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-[#231942]">{group.label}</p>
                {!isEmpty && <ChevronRight className={cn("mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform", isSelected && "rotate-90")} />}
              </div>
              <div className="mt-4 flex items-baseline justify-between gap-3">
                <p className={cn(
                  "text-xl font-semibold tabular-nums",
                  group.id === "moved-out-of-period" ? "text-[#5E548E]" : isAbove ? "text-[#177486]" : "text-[#9F3A4A]",
                )}>
                  {isEmpty
                    ? formatMoney(0, story.currencyCode)
                    : group.id === "moved-out-of-period"
                      ? formatMoney(group.fullDealValue, story.currencyCode)
                      : `${isAbove ? "+" : "−"}${formatMoney(group.gapContribution, story.currencyCode)}`}
                </p>
                <p className="text-[10px] font-medium text-gray-500">
                  {group.deals.length} {group.deals.length === 1 ? "Deal" : "Deals"}
                </p>
              </div>
              {group.id !== "moved-out-of-period" && (
                <p className="mt-1 text-[10px] text-gray-500">
                  {REVENUE_INTELLIGENCE_COPY.dealValue} {formatMoney(group.fullDealValue, story.currencyCode)}
                </p>
              )}
              {group.id === "moved-out-of-period" && (
                <p className="mt-1 text-[10px] text-gray-500">{REVENUE_INTELLIGENCE_COPY.dealValue}</p>
              )}
            </button>
          );
        })}
      </div>

      {selectedGroup && (
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
          <div className="flex flex-col gap-1 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-[#231942]">{selectedGroup.label}</p>
            <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <span>{selectedGroup.id === "moved-out-of-period" ? "Largest Deals first" : "Largest differences first"}</span>
              {selectedGroup.id !== "moved-out-of-period" && (
                <AnalysisInfoTooltip
                  compact
                  label="Forecast comparison"
                  text={REVENUE_INTELLIGENCE_COPY.forecast.forecastComparisonTooltip}
                />
              )}
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {visibleDeals.map(deal => (
              <div className={cn(
                "grid gap-3 px-4 py-3 lg:items-center",
                selectedGroup.id === "moved-out-of-period"
                  ? "lg:grid-cols-[minmax(220px,1.4fr)_repeat(3,minmax(120px,0.7fr))]"
                  : "lg:grid-cols-[minmax(220px,1.4fr)_repeat(4,minmax(105px,0.7fr))]",
              )} key={deal.id}>
                <div className="min-w-0">
                  <DealEntityName name={deal.name} onClick={onOpenDeal ? () => onOpenDeal(deal.id) : undefined} />
                  <span className="mt-1.5 inline-flex max-w-full rounded-full border border-[#D9D2E0] bg-[#F7F5F9] px-2 py-0.5 text-[9px] font-medium text-[#5E548E]">
                    Stage · <span className="ml-1 truncate">{deal.stage}</span>
                  </span>
                </div>
                <div>
                  <p className="text-[9px] text-gray-400">{REVENUE_INTELLIGENCE_COPY.dealValue}</p>
                  <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#231942]">{formatMoney(deal.fullValue, story.currencyCode)}</p>
                </div>
                {selectedGroup.id === "moved-out-of-period" ? (
                  <>
                    <div>
                      <p className="text-[9px] text-gray-400">CRM close</p>
                      <p className="mt-0.5 text-xs font-semibold text-[#231942]">{formatForecastDate(deal.crmCloseDate)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400">{REVENUE_INTELLIGENCE_COPY.forecast.revelateExpects}</p>
                      <p className="mt-0.5 text-xs font-semibold text-[#17697B]">{deal.revelateClose ?? "—"}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-[9px] text-gray-400">{REVENUE_INTELLIGENCE_COPY.forecast.crmContribution}</p>
                      <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#231942]">{formatMoney(deal.baselineContribution, story.currencyCode)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400">{REVENUE_INTELLIGENCE_COPY.forecast.revelateContribution}</p>
                      <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#17697B]">{formatMoney(deal.evidenceContribution, story.currencyCode)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400">{REVENUE_INTELLIGENCE_COPY.forecast.difference}</p>
                      <p className={cn("mt-0.5 text-xs font-semibold tabular-nums", selectedGroup.direction === "above" ? "text-[#177486]" : "text-[#9F3A4A]")}>
                        {selectedGroup.direction === "above" ? "+" : "−"}{formatMoney(deal.gapContribution, story.currencyCode)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          {selectedGroup.deals.length > 5 && (
            <div className="border-t border-gray-200 bg-white px-4 py-3">
              <button
                className="text-xs font-semibold text-[#5E548E] hover:text-[#3C2768]"
                onClick={() => setShowAll(current => !current)}
                type="button"
              >
                {showAll ? "Show top 5" : `Show all ${selectedGroup.deals.length} Deals`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ForecastContext({ story }: { story: ForecastDiagnosisStory }) {
  if (story.target === null) return null;

  const context = story.forecastContext;
  const coverage = context.evidenceWeightedCoverage;
  const targetAchieved = story.target !== null && context.wonToDate >= story.target;
  const contextItems = [
    {
      detail: null,
      label: "Target",
      value: story.target === null ? "—" : formatMoney(story.target, story.currencyCode),
    },
    {
      detail: null,
      label: "Won to date",
      value: formatMoney(context.wonToDate, story.currencyCode),
    },
    {
      detail: targetAchieved && story.target !== null
        ? `${formatMoney(context.wonToDate - story.target, story.currencyCode)} Won above Target`
        : null,
      label: "Remaining target",
      value: targetAchieved
        ? "Target achieved"
        : context.remainingTarget === null
          ? "—"
          : formatMoney(context.remainingTarget, story.currencyCode),
    },
  ];

  return (
    <section className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm lg:px-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5E548E]">Target context</p>
        {coverage && (
          <span className="w-fit rounded-full bg-[#F3F0F7] px-2.5 py-1 text-[10px] font-semibold text-[#5E548E]">
            Evidence-weighted
          </span>
        )}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {contextItems.map(item => (
          <div key={item.label} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-[10px] font-medium text-gray-500">{item.label}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-[#231942]">{item.value}</p>
            {item.detail && <p className="mt-1 text-[10px] leading-4 text-gray-400">{item.detail}</p>}
          </div>
        ))}
        <div className="rounded-lg border border-[#D9D2E0] bg-[#F9F7FB] px-4 py-3">
          <div className="flex items-center gap-1">
            <p className="text-[10px] font-medium text-gray-500">Remaining target coverage</p>
            <AnalysisInfoTooltip
              compact
              label="Remaining target coverage"
              text={coverage
                ? "The risk-adjusted value of all Deals Revelate expects to close this period."
                : "Remaining target coverage is unavailable for these filters."}
            />
          </div>
          <p className="mt-1 text-xl font-semibold tabular-nums text-[#231942]">
            {coverage?.status === "target_achieved"
              ? "Target achieved"
              : coverage?.coverageMultiple !== null && coverage?.coverageMultiple !== undefined
                ? `${coverage.coverageMultiple.toFixed(1)}×`
                : "—"}
          </p>
          <p className="mt-1 text-[10px] leading-4 text-gray-500">
            {coverage?.status === "target_achieved"
              ? "Won revenue already covers the Target"
              : coverage
                ? `${formatMoney(coverage.evidenceWeightedPipelineValue, story.currencyCode)} expected from ${coverage.eligibleDealCount} open ${coverage.eligibleDealCount === 1 ? "Deal" : "Deals"}`
              : "Unavailable for these filters"}
          </p>
        </div>
      </div>
    </section>
  );
}

function DriverEvidenceDialog({
  driver,
  onClose,
  open,
  story,
}: {
  driver: ForecastDiagnosisDriver | null;
  onClose: () => void;
  open: boolean;
  story: ForecastDiagnosisStory;
}) {
  const [dealDisplayMode, setDealDisplayMode] = useState<"top" | "material" | "all">("top");
  useEffect(() => setDealDisplayMode("top"), [driver?.id]);

  const { allDeals, materialDeals } = useMemo(() => {
    if (!driver) return { allDeals: [], materialDeals: [] };
    const dealsById = new Map(story.deals.map(deal => [deal.id, deal]));
    const getDeals = (ids: string[]) => ids.flatMap(id => {
      const deal = dealsById.get(id);
      return deal ? [deal] : [];
    });
    return {
      allDeals: getDeals(driver.allDealIds ?? driver.dealIds),
      materialDeals: getDeals(driver.dealIds),
    };
  }, [driver, story.deals]);

  const deals = dealDisplayMode === "all"
    ? allDeals
    : dealDisplayMode === "material"
      ? materialDeals
      : materialDeals.slice(0, 5);

  if (!driver) return null;
  const style = driverStyles[driver.status];

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: style.accent }}>{driver.label}</p>
          <DialogTitle className="pt-2 text-2xl leading-tight text-[#231942]">{driver.headline}</DialogTitle>
          <DialogDescription className="max-w-3xl text-sm leading-6 text-gray-600">{driver.explanation}</DialogDescription>
        </DialogHeader>

        <div className={`mt-2 grid gap-3 ${driver.metrics.length > 3 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
          {driver.metrics.map(metric => (
            <div key={metric.label} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-[11px] text-gray-500">{metric.label}</p>
              <p className="mt-1.5 text-lg font-semibold tabular-nums text-[#231942]">{metric.value}</p>
            </div>
          ))}
        </div>

        {deals.length > 0 ? (
          <div className="mt-5 overflow-hidden rounded-lg border border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div>
                <p className="text-xs font-semibold text-[#231942]">Deal evidence</p>
                <p className="mt-0.5 text-[10px] text-gray-500">
                  {allDeals.length <= 5
                    ? `Showing all ${allDeals.length} affected ${allDeals.length === 1 ? "Deal" : "Deals"}`
                    : `${allDeals.length} flagged · ${materialDeals.length} shown first`}
                </p>
              </div>
              {deals.length > 0 && (
                <p className="text-[10px] font-medium text-gray-500">
                  Showing {deals.length} of {allDeals.length}
                </p>
              )}
            </div>
            <div className="divide-y divide-gray-200">
              {deals.map(deal => (
                <div key={deal.id} className="grid gap-3 px-4 py-4 lg:grid-cols-[minmax(170px,0.7fr)_minmax(250px,1.5fr)_110px_110px] lg:items-center">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-gray-400">Deal</p>
                    <div className="mt-0.5">
                      <DealEntityName name={deal.name} />
                    </div>
                    <span className="mt-1.5 inline-flex max-w-full rounded-full border border-[#D9D2E0] bg-[#F7F5F9] px-2 py-0.5 text-[10px] font-medium text-[#5E548E]">
                      Stage · <span className="ml-1 truncate">{deal.stage}</span>
                    </span>
                  </div>
                  <p className="text-xs leading-5 text-gray-600">
                    {deal.driverSignals?.[driver.id] ?? deal.signal}
                  </p>
                  <div>
                    <p className="text-[10px] text-gray-400">CRM stage weighted</p>
                    <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#231942]">{formatMoney(deal.baselineContribution, story.currencyCode)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400">Included now</p>
                    <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#17697B]">{formatMoney(deal.evidenceContribution, story.currencyCode)}</p>
                    <p className="mt-1 text-[9px] text-gray-400">Full value {formatMoney(deal.fullValue, story.currencyCode)}</p>
                  </div>
                </div>
              ))}
            </div>
            {allDeals.length > 5 && (
              <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-white px-4 py-3">
                {dealDisplayMode === "top" && materialDeals.length > 5 && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-[#5E548E] hover:text-[#3C2768]"
                    onClick={() => setDealDisplayMode("material")}
                  >
                    Show {materialDeals.length - 5} more material {materialDeals.length - 5 === 1 ? "opportunity" : "opportunities"}
                  </button>
                )}
                {dealDisplayMode !== "all" && allDeals.length > materialDeals.length && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-[#5E548E] hover:text-[#3C2768]"
                    onClick={() => setDealDisplayMode("all")}
                  >
                    Show all {allDeals.length} flagged Deals
                  </button>
                )}
                {dealDisplayMode !== "top" && (
                  <button
                    type="button"
                    className="ml-auto text-xs font-medium text-gray-500 hover:text-[#3C2768]"
                    onClick={() => setDealDisplayMode("top")}
                  >
                    Show top 5
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={cn(
            "mt-5 flex gap-3 rounded-lg border p-4",
            driver.status === "unavailable"
              ? "border-gray-200 bg-gray-50"
              : "border-[#B9DDE3] bg-[#F2F9FA]",
          )}>
            {driver.status === "unavailable"
              ? <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
              : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1D819A]" />}
            <p className="text-xs leading-5 text-gray-600">
              {driver.status === "unavailable"
                ? "The required evidence is unavailable for this scope, so no healthy or adverse conclusion is inferred."
                : "No material Deal-level driver was found for these filters."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScenarioCard({
  forecastDependencies,
  onOpenDeal,
  onOpenForecastDependency,
  onOpenScenarioGroup,
  scenario,
  story,
}: {
  forecastDependencies: ForecastDependencyItem[];
  onOpenDeal?: (dealId: string) => void;
  onOpenForecastDependency?: (dependencyId: string) => void;
  onOpenScenarioGroup: (condition: ForecastDiagnosisScenarioCondition) => void;
  scenario: ForecastDiagnosisScenario;
  story: ForecastDiagnosisStory;
}) {
  const Icon = scenario.kind === "upside"
    ? ArrowUpRight
    : scenario.kind === "downside"
      ? ArrowDownRight
      : Crosshair;
  const styles = scenario.kind === "upside"
    ? { accent: "#177486", background: "#F4FAFB", border: "#B9DDE3" }
    : scenario.kind === "downside"
      ? { accent: "#9F3A4A", background: "#FCF6F7", border: "#E8C7CD" }
      : { accent: "#5E548E", background: "#F8F6FA", border: "#D8CCE3" };
  const scenarioAmount = formatMoneyParts(scenario.value, story.currencyCode);

  return (
    <article className="rounded-xl border p-4" style={{ backgroundColor: styles.background, borderColor: styles.border }}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.13em]" style={{ color: styles.accent }}>
        <Icon className="h-4 w-4" /> {scenario.label}
      </div>
      <div className="mt-3 grid gap-4 sm:grid-cols-[130px_minmax(0,1fr)] sm:items-start">
        <div>
          <p className="flex items-baseline gap-1 whitespace-nowrap tabular-nums text-[#2E254B]">
            <span className="text-3xl font-medium tracking-[-0.02em]">{scenarioAmount.amount}</span>
            <span className="text-xs font-medium tracking-[0.03em] text-[#756D82]">{scenarioAmount.unit}</span>
          </p>
          <p className="mt-1 text-[11px] font-semibold tabular-nums" style={{ color: styles.accent }}>
            {scenario.kind === "expected"
              ? REVENUE_INTELLIGENCE_COPY.forecast.currentForecast
              : scenario.delta === 0
                ? `No material ${scenario.kind}`
                : `${scenario.delta > 0 ? "+" : "−"}${formatMoney(Math.abs(scenario.delta), story.currencyCode)} vs ${REVENUE_INTELLIGENCE_COPY.forecast.revelateForecast}`}
          </p>
        </div>
        <div className="sm:border-l sm:border-black/10 sm:pl-4">
          {scenario.kind === "expected" ? (
            <div>
              <p className="text-[11px] leading-4 text-gray-600">{scenario.conditions[0]?.condition}</p>
              {forecastDependencies.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] font-semibold text-[#231942]">
                    {REVENUE_INTELLIGENCE_COPY.forecast.forecastDependencies}
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {forecastDependencies.map(dependency => (
                      <button
                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-[#DED5E8] bg-white/70 px-2.5 py-2 text-left transition hover:border-[#9F86C0] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E] focus-visible:ring-offset-1"
                        key={dependency.id}
                        onClick={() => onOpenForecastDependency?.(dependency.id)}
                        type="button"
                      >
                        <span className="min-w-0">
                          <span className="block text-[9px] font-medium text-gray-400">{dependency.dimension}</span>
                          <span className="block truncate text-[11px] font-semibold text-[#3C2768]">{dependency.label}</span>
                        </span>
                        <span className="shrink-0 text-right text-[10px] font-medium tabular-nums text-[#5E548E]">
                          {Math.round(dependency.share * 100)}% · {formatMoney(dependency.value, story.currencyCode)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <ul className="space-y-3">
                {scenario.conditions.map((item, index) => (
                  <li key={`${item.dealId ?? item.eventType ?? "general"}-${index}`} className="text-[11px] leading-4 text-gray-600">
                    {item.dealName && (
                      <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                        <DealEntityName
                          detail={[item.stage ? `Stage: ${item.stage}.` : null, item.valueExplanation].filter(Boolean).join(" ")}
                          name={item.dealName}
                          onClick={item.dealId && onOpenDeal ? () => onOpenDeal(item.dealId!) : undefined}
                        />
                      </div>
                    )}
                    {!item.dealName && item.dealCount && item.dealCount > 1 && (
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <button
                          className="text-[10px] font-semibold text-[#3C2768] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E] focus-visible:ring-offset-1"
                          onClick={() => onOpenScenarioGroup(item)}
                          type="button"
                        >
                          Deals · {item.dealCount}
                        </button>
                        {item.valueExplanation && (
                          <AnalysisInfoTooltip compact label={`${item.dealCount} Deal scenario details`} text={item.valueExplanation} />
                        )}
                      </div>
                    )}
                    <p className="break-words">
                      {item.impact !== undefined && item.impactCondition ? <>
                        <span className="font-semibold tabular-nums" style={{ color: item.impact < 0 ? "#9F3A4A" : "#177486" }}>
                          {item.impact < 0 ? "−" : "+"}{formatMoney(Math.abs(item.impact), story.currencyCode)}
                        </span>{" "}{item.impactCondition}
                      </> : item.condition}
                    </p>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>
    </article>
  );
}

function OutcomeScenarios({
  forecastDependencies,
  onOpenDeal,
  onOpenForecastDependency,
  onOpenScenarioGroup,
  story,
}: {
  forecastDependencies: ForecastDependencyItem[];
  onOpenDeal?: (dealId: string) => void;
  onOpenForecastDependency?: (dependencyId: string) => void;
  onOpenScenarioGroup: (condition: ForecastDiagnosisScenarioCondition) => void;
  story: ForecastDiagnosisStory;
}) {
  if (story.periodClosed) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm lg:px-6">
        <div className="flex items-center gap-2 text-sm font-medium text-[#4A4263]">
          <Clock3 className="h-4 w-4 text-[#775E97]" />
          Quarter closed
        </div>
      </section>
    );
  }

  const scenarioOrder: ForecastDiagnosisScenario["kind"][] = ["downside", "expected", "upside"];
  const scenarios = scenarioOrder.flatMap(kind => story.scenarios.filter(scenario => scenario.kind === kind));

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm lg:p-6">
      <h2 className="text-lg font-semibold text-[#231942]">What could move the outcome?</h2>
      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        {scenarios.map(scenario => (
          <ScenarioCard
            forecastDependencies={forecastDependencies}
            key={scenario.kind}
            onOpenDeal={onOpenDeal}
            onOpenForecastDependency={onOpenForecastDependency}
            onOpenScenarioGroup={onOpenScenarioGroup}
            scenario={scenario}
            story={story}
          />
        ))}
      </div>
    </section>
  );
}

function ScenarioDealDialog({
  condition,
  onClose,
  onOpenDeal,
  story,
}: {
  condition: ForecastDiagnosisScenarioCondition | null;
  onClose: () => void;
  onOpenDeal?: (dealId: string) => void;
  story: ForecastDiagnosisStory;
}) {
  const dealIds = new Set(condition?.dealIds ?? []);
  const deals = story.deals.filter(deal => dealIds.has(deal.id));

  return (
    <Dialog onOpenChange={open => !open && onClose()} open={Boolean(condition)}>
      <DialogContent className="max-h-[82vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{condition?.condition ?? "Scenario Deals"}</DialogTitle>
          <DialogDescription>{deals.length} Deals in this case.</DialogDescription>
        </DialogHeader>
        <div className="overflow-hidden rounded-xl border border-gray-200">
          {deals.map(deal => (
            <div className="grid gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center" key={deal.id}>
              <div className="min-w-0">
                <DealEntityName
                  detail={deal.signal}
                  name={deal.name}
                  onClick={onOpenDeal ? () => {
                    onClose();
                    onOpenDeal(deal.id);
                  } : undefined}
                />
                <p className="mt-1 text-[10px] text-gray-500">Stage · {deal.stage}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-[0.08em] text-gray-400">Revelate forecast</p>
                <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#17697B]">{formatMoney(deal.evidenceContribution, story.currencyCode)}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-[0.08em] text-gray-400">{REVENUE_INTELLIGENCE_COPY.dealValue}</p>
                <p className="mt-0.5 text-xs font-semibold tabular-nums text-[#231942]">{formatMoney(deal.fullValue, story.currencyCode)}</p>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ForecastDiagnosis({
  forecastDependencies = [],
  onOpenDeal,
  onOpenForecastDependency,
  showForecastContext = true,
  story,
}: {
  forecastDependencies?: ForecastDependencyItem[];
  onOpenDeal?: (dealId: string) => void;
  onOpenForecastDependency?: (dependencyId: string) => void;
  showForecastContext?: boolean;
  story: ForecastDiagnosisStory;
}) {
  const [selectedDriver, setSelectedDriver] = useState<ForecastDiagnosisDriver | null>(null);
  const [selectedScenarioCondition, setSelectedScenarioCondition] = useState<ForecastDiagnosisScenarioCondition | null>(null);

  return (
    <>
      <ForecastEvidenceFlow onOpen={setSelectedDriver} story={story} />
      <ForecastGapBreakdown onOpenDeal={onOpenDeal} story={story} />
      <OutcomeScenarios
        forecastDependencies={forecastDependencies}
        onOpenDeal={onOpenDeal}
        onOpenForecastDependency={onOpenForecastDependency}
        onOpenScenarioGroup={setSelectedScenarioCondition}
        story={story}
      />
      {showForecastContext && <ForecastContext story={story} />}
      <DriverEvidenceDialog
        driver={selectedDriver}
        onClose={() => setSelectedDriver(null)}
        open={Boolean(selectedDriver)}
        story={story}
      />
      <ScenarioDealDialog
        condition={selectedScenarioCondition}
        onClose={() => setSelectedScenarioCondition(null)}
        onOpenDeal={onOpenDeal}
        story={story}
      />
    </>
  );
}
